import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';

import {
  DEFAULT_LOCALE,
  LOCALES,
  localizedPath,
  localeFromPath,
  pathWithoutLocale,
  textDirection,
} from '../i18n/config';
import { SITE_URL } from '../site';

interface SeoProps {
  title: string;
  description?: string;
}

/** Title, description, canonical URL and hreflang alternates for an indexable public page. */
export function Seo({ title, description }: SeoProps) {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname) ?? DEFAULT_LOCALE;
  const path = pathWithoutLocale(pathname);
  const url = (lang: string) => `${SITE_URL}${localizedPath(lang, path)}`;

  return (
    <Helmet htmlAttributes={{ lang: locale, dir: textDirection(locale) }}>
      <title>{title}</title>
      {description !== undefined && <meta name="description" content={description} />}
      <link rel="canonical" href={url(locale)} />
      {LOCALES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url(lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url(DEFAULT_LOCALE)} />
    </Helmet>
  );
}

/** For private pages (dashboard, admin): never indexed. nginx also sends X-Robots-Tag. */
export function NoIndex({ title }: { title: string }) {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
}
